import React, { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

// Constants
import { useFlag } from 'containers/flags'
import { SKETCH_WEBSITE } from '@sketch/constants'

// Components
import { useStripe } from 'containers/StripeProvider'
import { useModalContext } from 'containers/ModalManager'
import { Button, Link } from '@sketch/components'
import Section from 'components/Section'

// Local Components
import {
  PlanOverviewPanel,
  BillingHistoryPanel,
  BillingWorkspacePanel,
  PanelSeparator,
  PaymentInformationPanel,
  WorkspaceSettingsSubscribePanel,
  PartnersPanel,
} from 'modules/workspace/components'

// Modals
import {
  CancelScheduleChangeModal,
  CancelWorkspaceSubscriptionModal,
  ChangeNextBillingCycleModal,
  ReactivateSubscriptionModal,
} from 'modules/workspace/modals'

// Types
import {
  BillingPlanFragment,
  BillingSeatsInfoFragment,
  BillingDetailsFragment,
  PaymentDetailsFragment,
  WorkspaceMembershipFragment,
} from '@sketch/gql-types'

// Usage of BillingStatus enum is needed to make the expected strings match the BE ones
// eslint-disable-next-line no-restricted-imports
import { BillingStatus } from '@sketch/gql-types/expansive'

// Utils
import {
  isWorkspaceSubscriptionActive,
  isWorkspaceSubscriptionTrialling,
  hasPaymentDetails,
} from 'modules/workspace/utils'
import { dateFormat } from '@sketch/utils'

// Styles
import {
  PartnersBanner,
  EducationBanner,
} from './WorkspaceSettingsBilling.styles'

interface BillingProps {
  currentPlan?: BillingPlanFragment
  credits: number
  seats: BillingSeatsInfoFragment
  workspaceId: string
  workspaceName: string
  customerId: string
  subscriptionStatus: BillingStatus
  nextBillingCycleDate?: string
  projectedCost?: number
  subscriptionEnd?: string
  billingDetails: BillingDetailsFragment
  paymentDetails?: PaymentDetailsFragment
  trialEnd: string
  pendingSCAToken?: string
  isWorkspaceSubscribed?: boolean
  onWorkspaceSettingRefresh: () => Promise<void>
  isFinance?: boolean
  isPartner?: boolean
  partner: WorkspaceMembershipFragment | null
  isEducationWorkspace?: boolean
}

/**
 *
 * Billing
 *
 * Renders the "Billing" page content in "Workspace Settings"
 */
export const WorkspaceSettingsBilling: React.FC<BillingProps> = props => {
  const {
    currentPlan,
    credits,
    seats,
    workspaceId,
    workspaceName,
    customerId,
    projectedCost,
    subscriptionStatus,
    subscriptionEnd,
    nextBillingCycleDate,
    billingDetails,
    paymentDetails,
    trialEnd,
    pendingSCAToken,
    onWorkspaceSettingRefresh,
    isFinance,
    isPartner,
    partner,
    isEducationWorkspace,
  } = props

  const { hash } = useLocation()

  const { load: loadStripe } = useStripe()
  const { showModal } = useModalContext()

  // Load stripe in order previously
  useEffect(() => {
    loadStripe()
  }, [loadStripe])

  const isWorkspaceOnTrial = isWorkspaceSubscriptionTrialling(
    subscriptionStatus
  )
  const isSubscriptionActive = isWorkspaceSubscriptionActive(subscriptionStatus)
  const customerHasPaymentDetails = hasPaymentDetails(paymentDetails)

  const canCancelSubscription =
    isSubscriptionActive &&
    !subscriptionEnd &&
    nextBillingCycleDate &&
    customerHasPaymentDetails &&
    !isFinance &&
    !isPartner

  // TODO: Remove sketch-partners FF
  // https://github.com/sketch-hq/Cloud/issues/12404
  const isSketchPartnersFFEnabled = useFlag('sketch-partners')

  const isPartneredWorkspace = isPartner || (partner && !!partner.acceptedAt)

  // Handle scroll to Sketch Partner section
  const sketchPartnerSectionRef = useRef<HTMLDivElement | null>(null)
  const handleScrollPosition = () => {
    if (isSketchPartnersFFEnabled && hash === '#sketch-partner') {
      window.requestAnimationFrame(() => {
        if (
          !sketchPartnerSectionRef.current ||
          !sketchPartnerSectionRef.current.scrollIntoView
        )
          return
        sketchPartnerSectionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      })
    }
  }

  const educationBannerExpireDate = nextBillingCycleDate ?? subscriptionEnd

  const shouldRenderPaymentDetails =
    isEducationWorkspace || (isPartneredWorkspace && !isPartner)
      ? false
      : credits !== 0 || (paymentDetails && paymentDetails.type !== 'NONE')

  const shouldRenderPartnersPanel =
    isSketchPartnersFFEnabled && !isEducationWorkspace && !isPartner

  const shouldRenderPlanOverview =
    !isEducationWorkspace && (!isPartneredWorkspace || isPartner)

  const shouldRenderPartnerBanner =
    !isPartner && partner && partner.acceptedAt && partner?.user?.name

  return (
    <>
      {isEducationWorkspace && (
        <EducationBanner type="information">
          Your Education Workspace is free until{' '}
          {dateFormat(new Date(educationBannerExpireDate!))}. <br />
          <Link href={`${SKETCH_WEBSITE}/support/contact/`} external>
            Get in touch
          </Link>{' '}
          if you have any questions or need for an extension.
        </EducationBanner>
      )}

      {shouldRenderPartnerBanner && (
        <PartnersBanner type="information">
          {partner!.user!.name} (Sketch Partner) manages billing in this
          Workspace.
          <br />
          If you have any questions, please email{' '}
          <Link external href="mailto:partners@sketch.com" isUnderlined>
            partners@sketch.com
          </Link>
        </PartnersBanner>
      )}

      {isPartner && !isSubscriptionActive && (
        <PartnersBanner type="warning">
          This Workspace subscription was canceled. Email{' '}
          <Link external href="mailto:partners@sketch.com" isUnderlined>
            partners@sketch.com
          </Link>{' '}
          to renew the subscription.
        </PartnersBanner>
      )}

      {!customerHasPaymentDetails && !isEducationWorkspace && (
        <PanelSeparator>
          <WorkspaceSettingsSubscribePanel workspaceId={workspaceId} />
        </PanelSeparator>
      )}

      {isPartner && (
        <PanelSeparator>
          <BillingWorkspacePanel
            workspaceName={workspaceName}
            workspaceId={workspaceId}
          />
        </PanelSeparator>
      )}

      {shouldRenderPlanOverview && (
        <PanelSeparator>
          <PlanOverviewPanel
            workspaceId={workspaceId}
            currentPlan={currentPlan}
            seats={seats}
            isWorkspaceSubscriptionActive={isSubscriptionActive}
            isWorkspaceOnTrial={isWorkspaceOnTrial}
            customerHasPaymentDetails={customerHasPaymentDetails}
            subscriptionEnd={subscriptionEnd}
            nextBillingCycleDate={nextBillingCycleDate}
            projectedCost={projectedCost}
            trialEndDate={trialEnd}
            onReactivateSubscription={() => {
              showModal(ReactivateSubscriptionModal, {
                currentPlan: currentPlan!,
                subscriptionEnd: subscriptionEnd!,
                customerId,
                workspaceId,
              })
            }}
            onSeatScheduleChange={() => {
              currentPlan &&
                showModal(ChangeNextBillingCycleModal, {
                  currentPlan,
                  customerId,
                  currentSeats: seats.currentSeatsTotal,
                  scheduledSeats: seats.scheduledSeatsTotal || undefined,
                  workspaceId,
                  variant: isPartner ? 'partner' : 'default',
                  nextBillingCycleDate,
                })
            }}
            onCancelSeatScheduleChange={() => {
              nextBillingCycleDate &&
                showModal(CancelScheduleChangeModal, {
                  workspaceId,
                  currentSeats: seats.currentSeatsTotal,
                  customerId,
                })
            }}
          />
        </PanelSeparator>
      )}

      {shouldRenderPaymentDetails && (
        <PanelSeparator>
          <PaymentInformationPanel
            onWorkspaceSettingRefresh={onWorkspaceSettingRefresh}
            credits={credits}
            customerId={customerId}
            billingDetails={billingDetails}
            paymentDetails={paymentDetails}
            subscriptionStatus={subscriptionStatus}
            pendingSCAToken={pendingSCAToken}
          />
        </PanelSeparator>
      )}
      <PanelSeparator>
        <BillingHistoryPanel
          customerId={customerId}
          workspaceId={workspaceId}
          trialEndDate={trialEnd}
          isWorkspaceOnTrial={isWorkspaceOnTrial}
          cancelAtPeriodEnd={subscriptionEnd}
          customerHasPaymentDetails={customerHasPaymentDetails}
          onLoadComplete={handleScrollPosition}
        />
      </PanelSeparator>

      {shouldRenderPartnersPanel && (
        <PanelSeparator ref={sketchPartnerSectionRef}>
          <PartnersPanel
            workspaceIdentifier={workspaceId}
            isSubscriptionActive={isSubscriptionActive}
            partner={partner!}
          />
        </PanelSeparator>
      )}

      {canCancelSubscription && (
        <PanelSeparator>
          {/* "data-testid="danger-zone-panel"" is used to keep consistency with the older panels in tests */}
          <Section
            data-testid="danger-zone-panel"
            title="Cancel Subscription"
            text="After canceling, you'll be able to use Sketch until the end of the current billing period."
            action={
              <Button
                onClick={() =>
                  showModal(CancelWorkspaceSubscriptionModal, {
                    customerId,
                    nextBillingCycleDate: nextBillingCycleDate!,
                    workspaceId,
                  })
                }
                size="32"
                variant="negative-secondary"
              >
                Cancel Subscription…
              </Button>
            }
          />
        </PanelSeparator>
      )}
    </>
  )
}
